import { createSlice } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { RestreamerState } from 'features/restreamer/types';
import { signalingRoomJoined } from 'features/room/actions';
import { roomTokenParsed } from 'features/room/roomSlice';
import { authorizeUser } from 'features/room/thunks/authorizeUser';
import {
  signalingRestreamerJoined,
  signalingRestreamingFailure,
  signalingRestreamingStopped,
} from 'features/restreamer/actions';

export const initialState: RestreamerState = {
  consentEnabled: false,
  isRestreamerSession: false,
  status: 'stopped',
  logoEnabled: false,
};

export const restreamerSlice = createSlice({
  name: 'restreamer',
  initialState,
  reducers: {
    restreamingRequested(state) {
      state.status = 'starting';
    },
    restreamingStarted(state) {
      state.status = 'started';
    },
    stopRestreamingRequest(state) {
      state.status = 'stopped';
    },
    stopRestreaming(state) {
      state.status = 'stopped';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signalingRoomJoined, (state, action) => {
        state.status = action.payload.restreaming.status;
        state.restreamer = action.payload.restreamer;
      })
      .addCase(signalingRestreamerJoined, (state, action) => {
        state.restreamer = action.payload;
      })
      .addCase(authorizeUser.fulfilled, (state, action) => {
        const { config, logoEnabled, consentMessageEnabled } = action.payload.settings.restreaming;

        state.logoEnabled = logoEnabled;
        state.consentEnabled = consentMessageEnabled;
        state.defaultConfig = config;
      })
      .addCase(signalingRestreamingStopped, (state) => {
        state.restreamer = undefined;
      })
      .addCase(signalingRestreamingFailure, (state) => {
        state.restreamer = undefined;
      })
      .addCase(roomTokenParsed, (state, action) => {
        if (action.payload.systemRole === 'restreamer') {
          state.isRestreamerSession = true;
        }
      });
  },
});

export const { restreamingRequested, restreamingStarted, stopRestreaming, stopRestreamingRequest } =
  restreamerSlice.actions;

export const selectIsRestreamerSession = (state: RootState) => state.restreamer.isRestreamerSession;

export const selectRestreamerStatus = (state: RootState) => state.restreamer.status;

export const selectRestreamerLogoVisibility = (state: RootState) => state.restreamer.logoEnabled;

export const selectRestreamer = (state: RootState) => state.restreamer.restreamer;

export const selectRestreamingConsentEnabled = (state: RootState) =>
  state.restreamer.consentEnabled;

export const selectRestreamingConsentMessageEnabled = (state: RootState) =>
  state.restreamer.consentEnabled;

export const selectRestreamingDefaultConfig = (state: RootState) => state.restreamer.defaultConfig;

export default restreamerSlice.reducer;
